<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import AutoComplete from 'primevue/autocomplete';
import Divider from 'primevue/divider';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const LANGUAGES = [
    { code: 'pt', label: 'Português (PT)' },
    { code: 'en', label: 'English (EN)' },
    { code: 'es', label: 'Español (ES)' }
];

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    templateType: {
        type: String,
        default: ''
    },
    newsletterId: {
        type: Number,
        default: null
    },
    existingData: {
        type: Object,
        default: null
    },
    clients: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:modelValue', 'saved']);

const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

const modalTitle = computed(() => {
    const typeLabel = $t(props.templateType);
    return props.newsletterId
        ? `${$t('Edit')} ${typeLabel}`
        : `${$t('Configure')} ${typeLabel}`;
});

// Template data
const templateData = ref({
    // Discount
    discountSites: null,
    // Best Sellers
    bestSellersPeriod: '',
    bestSellersCount: null,
    // Highlights
    highlightsCriteria: null,
    highlightsQuantity: null,
    // Common - button link (shared across languages)
    buttonLink: ''
});

// Language-specific data
const languageData = ref({
    pt: { title: '', message: '', buttonText: '' },
    en: { title: '', message: '', buttonText: '' },
    es: { title: '', message: '', buttonText: '' }
});

// Recipients data
const recipientsData = ref({
    sendType: null,
    selectedLanguages: [],
    selectedClients: []
});

const activeLanguageTab = ref(0);
const isSaving = ref(false);
const selectedClient = ref(null);
const filteredClients = ref([]);

const highlightOptions = computed(() => [
    { label: $t('Sites with more interests'), value: 'interests' },
    { label: $t('Sites with is Recommended'), value: 'recommended' },
    { label: $t('Sites with more projects'), value: 'projects' },
    { label: $t('Sites most added to cart'), value: 'cart' }
]);

const sendTypeOptions = computed(() => [
    {
        label: $t('ALL preference only'),
        value: 'All',
        description: $t('Clients who opted to receive all notifications')
    },
    {
        label: $t('ALL + MINIMAL'),
        value: 'Minimal',
        description: $t('Includes clients with minimal preference')
    },
    {
        label: $t('ALL + MINIMAL + DISABLED'),
        value: 'Disable',
        description: $t('Includes even clients who disabled notifications')
    },
    {
        label: $t('Custom selection'),
        value: 'Custom',
        description: $t('Manually select specific clients')
    }
]);

const isCustomType = computed(() => props.templateType === 'Custom');
const isDiscountType = computed(() => props.templateType === 'Discount');
const isBestSellersType = computed(() => props.templateType === 'Best Sellers');
const isHighlightsType = computed(() => props.templateType === 'Highlights');

// Check if at least one language has required fields filled
const hasAtLeastOneLanguageConfigured = computed(() => {
    return LANGUAGES.some(lang => {
        const data = languageData.value[lang.code];
        return data.title?.trim() && data.message?.trim();
    });
});

// Get configured languages
const configuredLanguages = computed(() => {
    return LANGUAGES
        .filter(lang => {
            const data = languageData.value[lang.code];
            return data.title?.trim() && data.message?.trim();
        })
        .map(lang => lang.code);
});

// Check if type-specific required fields are filled
const hasTypeSpecificFields = computed(() => {
    if (isDiscountType.value) {
        return templateData.value.discountSites !== null && templateData.value.discountSites > 0;
    }
    if (isBestSellersType.value) {
        return templateData.value.bestSellersPeriod &&
               templateData.value.bestSellersCount !== null &&
               templateData.value.bestSellersCount > 0;
    }
    if (isHighlightsType.value) {
        return templateData.value.highlightsCriteria &&
               templateData.value.highlightsQuantity !== null &&
               templateData.value.highlightsQuantity > 0;
    }
    // Custom type has no type-specific required fields
    return true;
});

// Recipients validation
const isRecipientsValid = computed(() => {
    if (!recipientsData.value.sendType) return true; // Recipients are optional
    if (recipientsData.value.selectedLanguages.length === 0) return false;
    if (recipientsData.value.sendType === 'Custom' && recipientsData.value.selectedClients.length === 0) return false;
    return true;
});

const isFormValid = computed(() => {
    return hasAtLeastOneLanguageConfigured.value && hasTypeSpecificFields.value && isRecipientsValid.value;
});

const isLanguageComplete = (langCode) => {
    const data = languageData.value[langCode];
    return data.title?.trim() && data.message?.trim();
};

// Available languages for recipients (only configured languages)
const availableRecipientsLanguages = computed(() => {
    return LANGUAGES.filter(lang => isLanguageComplete(lang.code));
});

// Client options filtered by selected languages
const clientOptions = computed(() => {
    const selectedIds = recipientsData.value.selectedClients.map(c => c.id);

    if (recipientsData.value.selectedLanguages.length === 0) {
        return [];
    }

    return props.clients
        .filter(client => {
            const clientLang = client.locale || 'pt';
            return recipientsData.value.selectedLanguages.includes(clientLang) || selectedIds.includes(client.id);
        })
        .map(client => {
            const fullName = [client.first_name, client.last_name]
                .filter(Boolean)
                .join(' ')
                .trim();

            return {
                label: fullName || client.name,
                value: client.id,
                id: client.id,
                name: fullName || client.name,
                email: client.email,
                language: client.locale || 'pt',
                notify_updates_via_email: client.notify_updates_via_email || 'DISABLED'
            };
        });
});

const searchClients = (event) => {
    const query = event.query.toLowerCase();
    filteredClients.value = clientOptions.value.filter(client =>
        client.label.toLowerCase().includes(query) ||
        client.email.toLowerCase().includes(query)
    ).filter(client =>
        !recipientsData.value.selectedClients.find(selected => selected.id === client.id)
    );
};

const addClient = (event) => {
    const client = event.value;
    if (client && !recipientsData.value.selectedClients.find(selected => selected.id === client.id)) {
        recipientsData.value.selectedClients.push(client);
    }
    selectedClient.value = null;
};

const removeClient = (index) => {
    recipientsData.value.selectedClients.splice(index, 1);
};

const resetForm = () => {
    templateData.value = {
        discountSites: null,
        bestSellersPeriod: '',
        bestSellersCount: null,
        highlightsCriteria: null,
        highlightsQuantity: null,
        buttonLink: ''
    };

    languageData.value = {
        pt: { title: '', message: '', buttonText: '' },
        en: { title: '', message: '', buttonText: '' },
        es: { title: '', message: '', buttonText: '' }
    };

    recipientsData.value = {
        sendType: null,
        selectedLanguages: [],
        selectedClients: []
    };

    activeLanguageTab.value = 0;
    isSaving.value = false;
};

const loadExistingData = () => {
    if (!props.existingData?.translations) return;

    const data = props.existingData;

    // Load translations
    data.translations.forEach(translation => {
        const lang = translation.lang;
        if (languageData.value[lang]) {
            languageData.value[lang] = {
                title: translation.title || '',
                message: translation.message || '',
                buttonText: translation.button_text || ''
            };
        }

        // Load type-specific data from first translation
        if (isDiscountType.value) {
            templateData.value.discountSites = translation.quantity;
            templateData.value.buttonLink = translation.button_link || '';
        } else if (isBestSellersType.value) {
            templateData.value.bestSellersPeriod = translation.period || '';
            templateData.value.bestSellersCount = translation.quantity;
            templateData.value.buttonLink = translation.button_link || '';
        } else if (isHighlightsType.value) {
            templateData.value.highlightsCriteria = translation.highlight_type;
            templateData.value.highlightsQuantity = translation.quantity;
            templateData.value.buttonLink = translation.button_link || '';
        } else if (isCustomType.value) {
            templateData.value.buttonLink = translation.button_link || '';
        }
    });

    // Load recipients data
    const activeTranslations = data.translations?.filter(t => t.send_type && t.send_type !== null) || [];
    if (activeTranslations.length > 0) {
        recipientsData.value.sendType = activeTranslations[0].send_type;
        recipientsData.value.selectedLanguages = activeTranslations.map(t => t.lang);

        if (activeTranslations[0].send_type === 'Custom' && data.recipients?.length > 0) {
            recipientsData.value.selectedClients = data.recipients.map(r => ({
                id: r.client.id,
                name: [r.client.first_name, r.client.last_name].filter(Boolean).join(' ').trim() || r.client.name,
                email: r.client.email,
                label: [r.client.first_name, r.client.last_name].filter(Boolean).join(' ').trim() || r.client.name,
                value: r.client.id,
                language: r.client.locale || 'pt',
                notify_updates_via_email: r.client.notify_updates_via_email || 'DISABLED'
            }));
        }
    }
};

const handleCancel = () => {
    visible.value = false;
};

const buildPayload = () => {
    const payload = {
        type: props.templateType,
        newsletterId: props.newsletterId,
        translations: {}
    };

    // Add language-specific data
    LANGUAGES.forEach(lang => {
        const data = languageData.value[lang.code];
        if (data.title?.trim() || data.message?.trim()) {
            payload.translations[lang.code] = {
                title: data.title,
                message: data.message,
                button_text: data.buttonText || null,
                button_link: templateData.value.buttonLink || null
            };
        }
    });

    // Add type-specific data
    if (isDiscountType.value) {
        payload.quantity = templateData.value.discountSites;
    } else if (isBestSellersType.value) {
        payload.period = templateData.value.bestSellersPeriod;
        payload.quantity = templateData.value.bestSellersCount;
    } else if (isHighlightsType.value) {
        payload.highlight_type = templateData.value.highlightsCriteria;
        payload.quantity = templateData.value.highlightsQuantity;
    }

    // Add recipients data if configured
    if (recipientsData.value.sendType && recipientsData.value.selectedLanguages.length > 0) {
        payload.recipients = {
            send_type: recipientsData.value.sendType,
            languages: recipientsData.value.selectedLanguages,
            selected_clients: recipientsData.value.sendType === 'Custom'
                ? recipientsData.value.selectedClients.map(c => c.id)
                : []
        };
    }

    return payload;
};

const handleSave = async () => {
    isSaving.value = true;

    try {
        const payload = buildPayload();

        const response = props.newsletterId
            ? await axios.put(`/admin/newsletter/${props.newsletterId}`, payload)
            : await axios.post('/admin/newsletter', payload);

        if (!response.data.success) {
            throw new Error(response.data.message || $t('Error saving newsletter'));
        }

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Newsletter saved successfully'),
            life: 3000
        });

        emit('saved', response.data.data?.id || props.newsletterId);

    } catch (error) {
        console.error('Error saving newsletter:', error);

        const errorMessage = error.response?.data?.message ||
                           error.message ||
                           $t('Error saving newsletter');

        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: errorMessage,
            life: 5000
        });
    } finally {
        isSaving.value = false;
    }
};

// Watch for modal open to load data
watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        resetForm();
        if (props.existingData) {
            loadExistingData();
        }
    }
});

// Watch for language changes to filter clients
watch(() => recipientsData.value.selectedLanguages, (newLanguages) => {
    if (recipientsData.value.sendType === 'Custom' && recipientsData.value.selectedClients.length > 0) {
        recipientsData.value.selectedClients = recipientsData.value.selectedClients.filter(client =>
            newLanguages.includes(client.language)
        );
    }
}, { deep: true });
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        :header="modalTitle"
        :style="{width: '60rem'}"
        :breakpoints="{'1199px': '80vw', '575px': '95vw'}"
        class="newsletter-edit-modal"
        :blockScroll="true"
        :dismissableMask="false"
    >
        <template #default>
            <div class="p-2">
                <!-- Type-Specific Settings -->
                <div v-if="!isCustomType" class="mb-6">
                    <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                        <i class="pi pi-cog text-primary-500"></i>
                        {{ $t('Email Settings') }}
                    </h3>

                    <!-- Discount Settings -->
                    <div v-if="isDiscountType" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="field">
                            <label class="block text-900 font-medium mb-2">
                                {{ $t('Number of Sites') }} <span class="text-red-500">*</span>
                            </label>
                            <InputNumber
                                v-model="templateData.discountSites"
                                class="w-full"
                                :min="1"
                                :max="50"
                                :placeholder="$t('Ex: 10')"
                            />
                        </div>
                    </div>

                    <!-- Best Sellers Settings -->
                    <div v-if="isBestSellersType" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="field">
                            <label class="block text-900 font-medium mb-2">
                                {{ $t('Period (days)') }} <span class="text-red-500">*</span>
                            </label>
                            <InputText
                                v-model="templateData.bestSellersPeriod"
                                class="w-full"
                                :placeholder="$t('Ex: 30')"
                            />
                        </div>
                        <div class="field">
                            <label class="block text-900 font-medium mb-2">
                                {{ $t('Number of Sites') }} <span class="text-red-500">*</span>
                            </label>
                            <InputNumber
                                v-model="templateData.bestSellersCount"
                                class="w-full"
                                :min="1"
                                :max="50"
                                :placeholder="$t('Ex: 10')"
                            />
                        </div>
                    </div>

                    <!-- Highlights Settings -->
                    <div v-if="isHighlightsType" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="field">
                            <label class="block text-900 font-medium mb-2">
                                {{ $t('Number of Sites') }} <span class="text-red-500">*</span>
                            </label>
                            <InputNumber
                                v-model="templateData.highlightsQuantity"
                                class="w-full"
                                :min="1"
                                :max="20"
                                :placeholder="$t('Ex: 5')"
                            />
                        </div>
                        <div class="field">
                            <label class="block text-900 font-medium mb-2">
                                {{ $t('Highlight Type') }} <span class="text-red-500">*</span>
                            </label>
                            <Dropdown
                                v-model="templateData.highlightsCriteria"
                                :options="highlightOptions"
                                optionLabel="label"
                                optionValue="value"
                                class="w-full"
                                :placeholder="$t('Select type')"
                            />
                        </div>
                    </div>
                </div>

                <!-- Button Configuration (Optional) -->
                <div class="mb-6">
                    <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                        <i class="pi pi-link text-primary-500"></i>
                        {{ $t('Button Configuration') }}
                        <span class="text-sm font-normal text-surface-500">({{ $t('Optional') }})</span>
                    </h3>
                    <div class="field">
                        <label class="block text-900 font-medium mb-2">{{ $t('Button Link') }}</label>
                        <InputText
                            v-model="templateData.buttonLink"
                            class="w-full"
                            :placeholder="$t('Ex: https://example.com')"
                        />
                        <small class="text-surface-500">{{ $t('Leave empty to hide the button in the email') }}</small>
                    </div>
                </div>

                <Divider />

                <!-- Language Templates -->
                <div class="mb-6">
                    <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                        <i class="pi pi-globe text-primary-500"></i>
                        {{ $t('Email Content') }}
                        <span class="text-sm font-normal text-surface-500">({{ $t('Configure at least one language') }})</span>
                    </h3>

                    <TabView v-model:activeIndex="activeLanguageTab">
                        <TabPanel v-for="lang in LANGUAGES" :key="lang.code">
                            <template #header>
                                <span class="flex items-center gap-2">
                                    <span>{{ lang.label }}</span>
                                    <i v-if="isLanguageComplete(lang.code)" class="pi pi-check-circle text-green-500"></i>
                                </span>
                            </template>

                            <div class="p-4">
                                <div class="field mb-4">
                                    <label class="block text-900 font-medium mb-2">
                                        {{ $t('Title') }} <span class="text-red-500">*</span>
                                    </label>
                                    <InputText
                                        v-model="languageData[lang.code].title"
                                        class="w-full"
                                        :placeholder="$t('Newsletter title')"
                                    />
                                </div>

                                <div class="field mb-4">
                                    <label class="block text-900 font-medium mb-2">
                                        {{ $t('Message') }} <span class="text-red-500">*</span>
                                    </label>
                                    <Textarea
                                        v-model="languageData[lang.code].message"
                                        rows="4"
                                        class="w-full"
                                        :placeholder="$t('Newsletter message content')"
                                    />
                                </div>

                                <div class="field">
                                    <label class="block text-900 font-medium mb-2">
                                        {{ $t('Button Text') }}
                                        <span class="text-sm font-normal text-surface-500">({{ $t('Optional') }})</span>
                                    </label>
                                    <InputText
                                        v-model="languageData[lang.code].buttonText"
                                        class="w-full"
                                        :placeholder="$t('Ex: Click here')"
                                    />
                                </div>
                            </div>
                        </TabPanel>
                    </TabView>
                </div>

                <Divider />

                <!-- Recipients Configuration -->
                <div>
                    <div class="border border-surface-200 rounded-lg">
                        <div class="p-4 bg-surface-50 border-b border-surface-200">
                            <div class="flex items-center gap-2">
                                <i class="pi pi-users text-primary-500"></i>
                                <span class="font-semibold">{{ $t('Recipients Configuration') }}</span>
                                <span class="text-sm font-normal text-surface-500">({{ $t('Optional') }})</span>
                                <i v-if="recipientsData.sendType && isRecipientsValid" class="pi pi-check-circle text-green-500 ml-2"></i>
                            </div>
                        </div>
                        <div class="p-4">
                            <!-- Send Type -->
                            <div class="mb-6">
                                <h4 class="text-md font-semibold mb-3">{{ $t('Send Type') }}</h4>
                                <Dropdown
                                    v-model="recipientsData.sendType"
                                    :options="sendTypeOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    :placeholder="$t('Select recipient type')"
                                    class="w-full"
                                    :autoOptionFocus="false"
                                >
                                    <template #option="slotProps">
                                        <div class="flex flex-column">
                                            <span class="font-medium me-2">{{ slotProps.option.label }}</span>
                                            <span class="text-sm text-surface-500">{{ slotProps.option.description }}</span>
                                        </div>
                                    </template>
                                </Dropdown>
                            </div>

                            <!-- Language Selection for Recipients -->
                            <div v-show="recipientsData.sendType" class="mb-6">
                                <h4 class="text-md font-semibold mb-3">
                                    {{ $t('Send to Languages') }} <span class="text-red-500">*</span>
                                </h4>

                                <p v-if="availableRecipientsLanguages.length === 0" class="text-sm text-orange-600 mb-3">
                                    {{ $t('Please configure at least one language template first') }}
                                </p>
                                <p v-else-if="availableRecipientsLanguages.length < 3" class="text-sm text-surface-500 mb-3">
                                    {{ $t('Only languages with configured templates are available') }}
                                </p>

                                <div class="flex flex-wrap gap-4">
                                    <div v-for="lang in LANGUAGES" :key="lang.code" class="flex align-items-center">
                                        <Checkbox
                                            v-model="recipientsData.selectedLanguages"
                                            :inputId="`lang-${lang.code}`"
                                            :value="lang.code"
                                            :disabled="!isLanguageComplete(lang.code)"
                                        />
                                        <label
                                            :for="`lang-${lang.code}`"
                                            class="ml-2"
                                            :class="{ 'text-surface-400': !isLanguageComplete(lang.code) }"
                                        >
                                            {{ lang.label }}
                                            <span v-if="!isLanguageComplete(lang.code)" class="text-xs text-surface-400 ml-1">
                                                ({{ $t('no template') }})
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <!-- Custom Client Selection -->
                            <div v-show="recipientsData.sendType === 'Custom'">
                                <h4 class="text-md font-semibold mb-3">
                                    {{ $t('Select Clients') }} <span class="text-red-500">*</span>
                                </h4>

                                <p v-if="recipientsData.selectedLanguages.length === 0" class="text-sm text-orange-600 mb-3">
                                    {{ $t('Please select at least one language first') }}
                                </p>
                                <p v-else class="text-sm text-surface-500 mb-3">
                                    {{ $t('Only clients matching the selected languages will be shown') }}
                                </p>

                                <AutoComplete
                                    v-model="selectedClient"
                                    :suggestions="filteredClients"
                                    @complete="searchClients"
                                    @item-select="addClient"
                                    optionLabel="label"
                                    :placeholder="$t('Type client name to search...')"
                                    class="w-full mb-4"
                                    :dropdown="true"
                                    :forceSelection="false"
                                    :disabled="recipientsData.selectedLanguages.length === 0"
                                />

                                <!-- Selected Clients List -->
                                <div v-if="recipientsData.selectedClients.length > 0" class="mt-4">
                                    <h5 class="text-sm font-medium mb-2">
                                        {{ $t('Selected Clients') }} ({{ recipientsData.selectedClients.length }})
                                    </h5>
                                    <div class="border border-surface-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                                        <div
                                            v-for="(client, index) in recipientsData.selectedClients"
                                            :key="client.id"
                                            class="flex justify-between items-center p-2 border-b border-surface-100 last:border-b-0"
                                        >
                                            <div>
                                                <div class="font-medium">{{ client.name }}</div>
                                                <div class="text-sm text-surface-500">{{ client.email }}</div>
                                            </div>
                                            <div class="flex items-center gap-2">
                                                <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                    {{ client.language.toUpperCase() }}
                                                </span>
                                                <Button
                                                    icon="pi pi-times"
                                                    severity="danger"
                                                    text
                                                    rounded
                                                    size="small"
                                                    @click="removeClient(index)"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    text
                    @click="handleCancel"
                />
                <Button
                    :label="$t('Save All')"
                    icon="pi pi-check"
                    severity="success"
                    :disabled="!isFormValid || isSaving"
                    :loading="isSaving"
                    @click="handleSave"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
:deep(.newsletter-edit-modal .p-dialog-content) {
    padding: 1rem;
}

:deep(.p-accordion-header-link) {
    padding: 1rem;
}

.max-h-48 {
    max-height: 12rem;
}
</style>
